import { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, ButtonGroup, Alert } from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import SearchBar from "components/common/SearchBar";
import useIsMobile from "components/hooks/useIsMobile";
import AdvisorRanking from "components/matchmaking/AdvisorRanking";
import { ArrowLeft } from "lucide-react";
import RematchCard from "components/rematching/RematchCard";
import RematchStatusMenu from "components/rematching/RematchStatusMenu";
import {
  RematchSessionStatus,
  RematchSession,
} from "protogen/advisors_service_pb";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useListRematchSessions } from "services/admin";
import Loading from "components/common/Loading";

const ACTIVE_REMATCH_STATUSES = [
  RematchSessionStatus.REMATCH_ACTIVE,
  RematchSessionStatus.REMATCH_ACTIVE_DRAFT,
  RematchSessionStatus.REMATCH_CLIENT_EMAIL_SENT,
  RematchSessionStatus.REMATCH_CLIENT_FOLLOWUP_SENT,
  RematchSessionStatus.REMATCH_ADVISOR_SELECTED,
] as const;

const sessionMatchesQuery = (
  session: RematchSession,
  normalizedQuery: string,
) => {
  if (!normalizedQuery) return true;

  const searchableValues: string[] = [];
  const pushValue = (value?: string | null) => {
    if (!value) return;
    const trimmed = value.trim();
    if (trimmed) {
      searchableValues.push(trimmed.toLowerCase());
    }
  };

  pushValue(session.family?.name);

  if (session.primaryAdvisor) {
    const { firstName, lastName } = session.primaryAdvisor;
    pushValue(firstName);
    pushValue(lastName);
    pushValue(`${firstName} ${lastName}`);
  }

  session.family?.familyMembers?.forEach((member) => {
    pushValue(member.firstName);
    pushValue(member.lastName);
    pushValue(`${member.firstName} ${member.lastName}`);
    pushValue(member.primaryEmail);
    pushValue(member.primaryPhone);
  });

  return searchableValues.some((value) => value.includes(normalizedQuery));
};

export default () => {
  const isMobile = useIsMobile();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<RematchSessionStatus>(
    RematchSessionStatus.REMATCH_ACTIVE,
  );
  const selectedSessionRef = params.sessionRef || null;
  const [query, setQuery] = useState<string>(
    () => searchParams.get("query") || "",
  );
  const [selectedSession, setSelectedSession] = useState<RematchSession | null>(
    null,
  );
  const { request, data, loading } = useListRematchSessions();

  useEffect(() => {
    refresh();
  }, [statusFilter, selectedSessionRef]);

  useEffect(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (query) {
        newParams.set("query", query);
      } else {
        newParams.delete("query");
      }
      return newParams;
    });
  }, [query, setSearchParams]);

  useEffect(() => {
    if (!selectedSessionRef) {
      setSelectedSession(null);
      return;
    }
    const session = data?.sessions?.find((s) => s.ref === selectedSessionRef);
    if (session) {
      setSelectedSession(session);
    }
  }, [selectedSessionRef, data]);

  // A deep-linked session may sit outside the active status filter, so fetch every status while focused.
  const refresh = async () => {
    const statuses = selectedSessionRef
      ? []
      : statusFilter === RematchSessionStatus.REMATCH_ACTIVE
        ? [...ACTIVE_REMATCH_STATUSES]
        : statusFilter === RematchSessionStatus.REMATCH_STATUS_NOT_SET
          ? []
          : [statusFilter];
    await request({ statuses });
  };

  // Sort sessions: immediate rematches first, then non-draft, then draft, with billing date within each group
  const sortedSessions = useMemo(() => {
    const sessions = [...(data?.sessions || [])];
    return sessions.sort((a, b) => {
      // Immediate rematches go to the top
      if (a.immediateRematch && !b.immediateRematch) return -1;
      if (!a.immediateRematch && b.immediateRematch) return 1;

      // Within immediate rematch group or non-immediate group, sort by draft status
      const aIsDraft = a.status === "active_draft";
      const bIsDraft = b.status === "active_draft";

      if (!aIsDraft && bIsDraft) return -1;
      if (aIsDraft && !bIsDraft) return 1;

      // Within same draft status, sort by next billing date (earliest first)
      const aDate = a.family?.nextBillingDate;
      const bDate = b.family?.nextBillingDate;

      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;

      // Parse dates and compare
      const aTime = new Date(aDate).getTime();
      const bTime = new Date(bDate).getTime();
      return aTime - bTime;
    });
  }, [data?.sessions]);

  const filteredSessions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return sortedSessions;
    }
    return sortedSessions.filter((session) =>
      sessionMatchesQuery(session, normalizedQuery),
    );
  }, [sortedSessions, query]);

  const querySearchString = query ? `?query=${encodeURIComponent(query)}` : "";

  const openSession = (sessionRef: string) => {
    navigate(`/tools/rematching/${sessionRef}${querySearchString}`);
  };

  const closeSession = () => {
    navigate(`/tools/rematching${querySearchString}`);
  };

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tools", link: "/tools" },
              ...(selectedSessionRef
                ? [
                    {
                      name: "Rematching",
                      link: `/tools/rematching${querySearchString}`,
                    },
                  ]
                : []),
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Box
              display="flex"
              flexDirection="row"
              gap="12px"
              alignContent="center"
            >
              <Typography variant="display">Rematching</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: selectedSessionRef ? "none" : "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          gap: isMobile ? "12px" : "24px",
          marginBottom: "16px",
        }}
      >
        <SearchBar
          initialQuery={query}
          fullWidth={isMobile}
          autofocus={true}
          onQuery={setQuery}
          onKeyPress={true}
          sx={{ marginLeft: "0" }}
        />
        <ButtonGroup
          style={{
            display: "flex",
            ...(isMobile && {
              width: "100%",
            }),
          }}
        >
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={
              statusFilter === RematchSessionStatus.REMATCH_ACTIVE
                ? "buttonGroupSelected"
                : "buttonGroup"
            }
            onClick={() => setStatusFilter(RematchSessionStatus.REMATCH_ACTIVE)}
          >
            Active
          </Button>
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={
              statusFilter === RematchSessionStatus.REMATCH_CANCELED
                ? "buttonGroupSelected"
                : "buttonGroup"
            }
            onClick={() =>
              setStatusFilter(RematchSessionStatus.REMATCH_CANCELED)
            }
          >
            Canceled
          </Button>
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={
              statusFilter === RematchSessionStatus.REMATCH_COMPLETE
                ? "buttonGroupSelected"
                : "buttonGroup"
            }
            onClick={() =>
              setStatusFilter(RematchSessionStatus.REMATCH_COMPLETE)
            }
          >
            Complete
          </Button>
          <Button
            sx={{
              flex: "1",
              width: "80px",
              minWidth: "unset",
            }}
            variant={
              statusFilter === RematchSessionStatus.REMATCH_STATUS_NOT_SET
                ? "buttonGroupSelected"
                : "buttonGroup"
            }
            onClick={() =>
              setStatusFilter(RematchSessionStatus.REMATCH_STATUS_NOT_SET)
            }
          >
            All
          </Button>
        </ButtonGroup>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
          paddingBottom: "16px",
        }}
      >
        {(loading || !data) && !selectedSession && <Loading />}
        {selectedSessionRef ? (
          <>
            <Box>
              <Button
                variant="text"
                startIcon={<ArrowLeft size={16} />}
                onClick={closeSession}
                sx={{ paddingLeft: "0" }}
              >
                All rematches
              </Button>
            </Box>
            {!loading && data && !selectedSession && (
              <Alert severity="info">Rematch session not found</Alert>
            )}
            {selectedSession && (
              <>
                <RematchCard
                  key={selectedSession.ref}
                  session={selectedSession}
                  defaultOpen={true}
                  onUpdate={() => refresh()}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <RematchStatusMenu
                    session={selectedSession}
                    onUpdated={() => refresh()}
                  />
                </Box>
                <AdvisorRanking
                  familyRef={selectedSession.familyRef}
                  rematchSessionRef={selectedSession.ref}
                  rematchSession={selectedSession}
                  onStarted={() => refresh()}
                  onRematchSessionUpdate={(updatedSession) => {
                    setSelectedSession(updatedSession);
                    refresh();
                  }}
                />
              </>
            )}
          </>
        ) : (
          <>
            {!loading && data && !filteredSessions.length && (
              <Alert severity="info">
                {query && sortedSessions.length
                  ? "No rematch sessions match your search"
                  : "No rematch sessions found"}
              </Alert>
            )}
            {!loading &&
              filteredSessions.map((session) => (
                <RematchCard
                  key={session.ref}
                  session={session}
                  onExpand={() => openSession(session.ref)}
                  onUpdate={() => refresh()}
                />
              ))}
          </>
        )}
      </Box>
    </Box>
  );
};
